import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TableRepository } from '@database/workspace';
import { QueryParamsProvider } from '@domain/routing';
import {
	AppVariables,
	DateTimeService,
	ExpandableCard,
	ExpandableCardProperty,
	FUSION_DATE_TIME_FORMATTING,
	HorizontalMenuState,
	ScreenOrientationService,
	TabItem,
	TableModel
} from '@shared';
import { Observable, Subscription, firstValueFrom, iif, map, of, switchMap } from 'rxjs';
import { SampleTypeProvider, TableService, TableTypeProvider } from '@domain/business-layer';
import { TranslateService } from '@ngx-translate/core';
import { IonContent, ModalController } from '@ionic/angular';
import { SignatureComponent } from '../signature';
import {
	FileExporterService,
	FileExporterTableMetadata,
	TableExpandableCards,
	TimeDataFormattingService
} from '@domain/file-exporter';

@Component({
	selector: 'app-entry-preview',
	templateUrl: './entry-preview.component.html',
	styleUrls: ['./entry-preview.component.scss'],
	providers: [HorizontalMenuState]
})
export class EntryPreviewComponent implements OnInit, OnDestroy {
	@ViewChild('content') public content: IonContent;

	public tableTitle: string;
	public workfileId: string;
	public entryId: number;
	public parentEntryId: number;
	public childTables: TabItem[];
	public projectNumber: string;
	public childTableItems: ExpandableCardProperty[][];
	public areColumnsDisplayedVertically = true;
	public base64Signature: string;
	public isOrientationLandscape: boolean;

	private entityName: string;
	private subscriptions: Subscription[] = [];
	private activeChildTable: string;

	public constructor(
		private tableRepository: TableRepository,
		private queryParamsProvider: QueryParamsProvider,
		private tableService: TableService,
		private horizontalMenuState: HorizontalMenuState,
		private tableTypeProvider: TableTypeProvider,
		private sampleTypeProvider: SampleTypeProvider,
		private dateTimeService: DateTimeService,
		private translateService: TranslateService,
		private modalController: ModalController,
		private screenOrientationService: ScreenOrientationService,
		private fileExporterService: FileExporterService,
		private timeDataFormattingService: TimeDataFormattingService
	) {}

	public reportName$(): Observable<string> {
		const timestamp: string = this.dateTimeService.getCurrentDateInFormat(FUSION_DATE_TIME_FORMATTING);

		return this.tableRepository
			.getTableTitle$(this.entityName)
			.pipe(map((title: string) => `${title} - ${timestamp}`));
	}

	public ngOnInit(): void {
		this.workfileId = this.queryParamsProvider.workfileId;
		this.entityName = this.queryParamsProvider.parentTableName;
		this.entryId = this.queryParamsProvider.childEntryId
			? Number(this.queryParamsProvider.childEntryId)
			: Number(this.queryParamsProvider.entryId);
		this.projectNumber = this.queryParamsProvider.projectNumber;

		this.handleScreenOrientation();
		this.subscriptions.push(
			this.tableService.getChildTables$(this.entityName).subscribe((childTables: TableModel[]) => {
				const parentTableTitle = this.translateService.instant('entry.report.parentDetails');
				const parentTableId = 'PARENT_TABLE';

				this.childTables = this.mapTableModelsIntoTabItems(childTables);
				this.childTables = this.appendOnFirstPosition(this.childTables, {
					id: parentTableId,
					title: parentTableTitle,
					tableName: this.entityName
				});
			})
		);

		this.subscriptions.push(
			this.horizontalMenuState.activeSelection$
				.pipe(
					switchMap((activeChildTable: string) => {
						if (!activeChildTable) {
							return of(null);
						}

						this.activeChildTable = activeChildTable;
						const isParentTableActive: boolean =
							this.childTables.length > 0 && this.childTables[0].tableName === this.activeChildTable;

						return this.tableTypeProvider.isSampleTable$(this.activeChildTable).pipe(
							switchMap((isSampleTable: boolean) =>
								isSampleTable ? this.sampleTypeProvider.getSampleTypes$() : of(null)
							),
							switchMap((sampleTypes: { [key: string]: boolean }) =>
								iif(
									() => (sampleTypes ? true : false),
									this.tableService.getChildTableFieldsWithStandardsForReportPreview$(
										this.workfileId,
										this.projectNumber,
										this.entityName,
										this.entryId,
										activeChildTable,
										isParentTableActive,
										sampleTypes
									),
									this.tableService.getChildTableFields$(
										this.workfileId,
										this.projectNumber,
										this.entityName,
										this.entryId,
										activeChildTable,
										isParentTableActive,
										sampleTypes
									)
								)
							)
						);
					})
				)
				.subscribe((tableExpandableCards: TableExpandableCards) => {
					if (!tableExpandableCards) {
						this.childTableItems = [];
						return;
					}

					const { expandableCards } = this.timeDataFormattingService.formatTimeFields(tableExpandableCards);
					this.areColumnsDisplayedVertically = this.childTables[0].tableName === this.activeChildTable;
					this.childTableItems = expandableCards.map(
						(expandableCard: ExpandableCard) => expandableCard.properties
					);
				})
		);
	}

	public ngOnDestroy(): void {
		this.subscriptions.forEach((subscription) => subscription.unsubscribe());
	}
	
	public async signPreview(): Promise<void> {
		const modal = await this.modalController.create({
			component: SignatureComponent,
			cssClass: 'signature-page',
			backdropDismiss: true,
			initialBreakpoint: 1,
			showBackdrop: true
		});

		await modal.present();
		const response = await modal.onDidDismiss<string>();
		this.base64Signature = response.data;
	}

	public async export(): Promise<void> {
		const reportName: string = await firstValueFrom(this.reportName$());
		const fileExporterTablesMetadata: FileExporterTableMetadata[] = this.childTables.map(
			(childTable: TabItem, index: number) => ({
				title: childTable.title,
				tableName: childTable.tableName,
				isParent: index === 0
			})
		);
		this.fileExporterService.export(
			fileExporterTablesMetadata,
			{
				workfileId: this.workfileId,
				entryId: this.entryId,
				projectNumber: this.projectNumber,
				entityName: this.entityName
			},
			reportName
		);
	}

	public close(): void {
		this.modalController.dismiss(this.base64Signature);
	}

	private mapTableModelsIntoTabItems(tableModels: TableModel[]): TabItem[] {
		return tableModels.map((tableModel: TableModel) => ({
			id: tableModel.child_table_ids,
			title: tableModel.tab_title,
			tableName: tableModel.table_name
		}));
	}

	private handleScreenOrientation(): void {
		this.subscriptions.push(
			this.screenOrientationService.isOrientationLandscape$.subscribe(
				(isOrientationLandscape: boolean) => (this.isOrientationLandscape = isOrientationLandscape)
			)
		);
	}

	private appendOnFirstPosition(arr: TabItem[], newElement: TabItem): TabItem[] {
		return [newElement, ...arr];
	}
}
